/** Pure cadence/CPU policy. Only visible, completed windows are observations. */
export const TIER_ORDER = ['low', 'medium', 'high', 'ultra'];
/** A tier failed twice inside this window is not retried. */
const FAILURE_WINDOW_MS = 120000;
export function selectQuality({ cores = 2, touch = false, override = null } = {}) {
  const pinned = TIER_ORDER.includes(override);
  const tier = pinned ? override : cores < 4 ? 'low' : 'medium';
  // Every phone reports 6–8 cores, so a core count never kept one off `high`:
  // it climbed there after 15 s of headroom, the second scene pass, outline
  // pass, shadows and 1.5x resolution sank it, and it fell back, recompiling
  // every lit shader both ways. Touch devices top out at medium.
  const maxTier = pinned ? override : touch ? 'medium' : 'high';
  return { tier, maxTier, shadowSize: tier === 'ultra' ? 2048 : 1024, pinned };
}

export class QualityPolicy {
  constructor({ tier = 'medium', maxTier = 'high', pinned = false, warmupMs = 10000 } = {}) {
    Object.assign(this, { tier, maxTier, pinned, warmupMs });
    this.capFps = 0;
    this.visibleSince = null;
    this.degradeSince = null;
    this.recoverSince = null;
    /** tier -> times this device fell off it, within FAILURE_WINDOW_MS. */
    this.failures = {};
  }
  observe({ now, fps, cpuMs, visible = true }) {
    if (!visible) {
      this.visibleSince = this.degradeSince = this.recoverSince = null;
      return this.tier;
    }
    if (!Number.isFinite(fps) || fps <= 0 || !Number.isFinite(cpuMs)) return this.tier;
    this.visibleSince ??= now;
    this.capFps = Math.max(this.capFps, fps);
    if (this.pinned || now - this.visibleSince < this.warmupMs) return this.tier;
    // Only the recognisable 30 Hz cadence earns the cap exception. Observed
    // 40–50 FPS is not evidence of a display cap, even with low CPU submission.
    const capped30 = this.capFps >= 28 && this.capFps <= 32 && fps >= 28 && fps <= 32;
    const budget = capped30 ? 1000 / 30 : 1000 / 60;
    const target = capped30 ? 30 : 60;
    const missed = fps < target * 0.85 || cpuMs >= budget * 0.85;
    const headroom = fps >= target * 0.93 && cpuMs < budget * 0.6;
    const index = TIER_ORDER.indexOf(this.tier);
    if (missed) {
      this.recoverSince = null;
      this.degradeSince ??= now;
      if (now - this.degradeSince >= 3000 && index > 0) {
        // One retry, then the ceiling comes down. Each tier change rebuilds
        // shaders, so a device that cannot hold a tier must not keep
        // bouncing off it every 18 seconds.
        // Two falls close together, not two in a session: a map load or a GC
        // pause every few minutes is not a device that cannot hold the tier.
        const recent = (this.failures[this.tier] ?? []).filter((t) => now - t < FAILURE_WINDOW_MS);
        recent.push(now);
        this.failures[this.tier] = recent;
        if (recent.length >= 2) this.maxTier = TIER_ORDER[index - 1];
        this.tier = TIER_ORDER[index - 1];
        this.degradeSince = null;
      }
    } else if (headroom) {
      this.degradeSince = null;
      this.recoverSince ??= now;
      if (now - this.recoverSince >= 15000 && index < TIER_ORDER.indexOf(this.maxTier)) {
        this.tier = TIER_ORDER[index + 1];
        this.recoverSince = null;
      }
    } else {
      this.degradeSince = this.recoverSince = null;
    }
    return this.tier;
  }
}
